import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault } from '../lib/vault/vaultApi';
import {
  createExecutor,
  inviteExecutor,
  listExecutors,
  ORDER_COPY,
  type ExecutorOrder,
} from '../lib/executors/executorApi';
import './guardians.css';

export default function ExecutorFormScreen() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // The slot to fill comes from the link the owner followed (?order=1|2). If a
  // primary already exists we quietly fall back to the backup slot, and vice
  // versa, so the form always targets a free slot.
  const requested = params.get('order') === '2' ? 2 : 1;

  const [vaultId, setVaultId] = useState<string | null>(null);
  const [order, setOrder] = useState<ExecutorOrder>(requested);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const dash = await getMyVault(api);
        if (cancelled) return;
        setVaultId(dash.vault.id);
        const res = await listExecutors(api, dash.vault.id);
        if (cancelled) return;
        const live = res.executors.filter((e) => e.status === 'invited' || e.status === 'active');
        const taken = new Set(live.map((e) => e.order_index));
        // Land on a free slot: honour the requested one if free, else the other.
        if (taken.has(requested)) {
          setOrder(requested === 1 ? 2 : 1);
        } else {
          setOrder(requested);
        }
      } catch {
        if (!cancelled) setError('We could not open your vault just now.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, requested]);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (vaultId === null) return;
    if (name.trim() === '' || email.trim() === '') {
      setError('We need their name and their email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await createExecutor(api, vaultId, {
        order_index: order,
        executor_name: name.trim(),
        executor_email: email.trim(),
      });
      // Designating them does not tell them — send the invitation now, so a
      // named executor who never hears about it can't become a silent gap in
      // the one chain you are relying on.
      await inviteExecutor(api, vaultId, res.executor.id);
      navigate('/executors', { replace: true });
    } catch {
      setError('We could not add them. That slot may already be taken.');
      setBusy(false);
    }
  }

  return (
    <section className="guard">
      <p className="guard__kicker">A person you trust</p>
      <h1 className="guard__title">
        Name your {order === 1 ? 'primary' : 'backup'} executor.
      </h1>
      <p className="guard__sub">
        They will be asked to accept. Once they do, they can authorize the release of your vault when
        you are gone — a single named administrator, not a vote. They can never read what is inside;
        each message goes only to the person it was written for.
      </p>

      <form onSubmit={(e) => void onSubmit(e)}>
        <label className="guard__field">
          <span className="guard__label">Their name</span>
          <input
            className="guard__input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </label>

        <label className="guard__field">
          <span className="guard__label">Their email</span>
          <input
            className="guard__input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>

        <p className="guard__role" style={{ marginTop: 8 }}>
          <strong>{ORDER_COPY[order].label}</strong> — {ORDER_COPY[order].blurb}
        </p>

        {error !== null && (
          <p role="alert" className="guard__alert">
            {error}
          </p>
        )}

        <div className="guard__actions">
          <button type="button" className="la-btn-secondary" onClick={() => navigate('/executors')}>
            Back
          </button>
          <div className="la-cta-wrap">
            <button type="submit" className="la-btn-primary" disabled={busy || vaultId === null}>
              {busy ? 'Sending' : 'Ask them'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}
